<script setup lang="ts">
import { computed, ref, useSlots } from 'vue'
import type { HTMLAttributes } from 'vue'
import { cn } from '../../lib/utils'
import { searchSidebarVariants } from './index'

interface Props {
  /** Masque le titre interne — utile quand le conteneur porte déjà un titre (drawer, modale). */
  showTitle?: boolean
  cancelLabel?: string
  submitLabel?: string
  moreLabel?: string
  lessLabel?: string
  class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
  showTitle: true,
  cancelLabel: 'Annuler',
  submitLabel: 'Trouver',
  moreLabel: 'de critères',
  lessLabel: 'de critères',
})

const emit = defineEmits<{
  cancel: []
  submit: []
  'update:expanded': [value: boolean]
}>()

const slots = useSlots()
const hasMore = computed(() => !!slots.more)

const isExpanded = ref(false)
const styles = searchSidebarVariants()

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
  emit('update:expanded', isExpanded.value)
}
</script>

<template>
  <aside :class="cn(styles.root(), props.class)">
    <form
      class="contents"
      role="search"
      aria-label="Rechercher"
      @submit.prevent="emit('submit')"
    >
      <div :class="styles.body()">
        <h2 v-if="showTitle" :class="styles.title()">Rechercher</h2>

        <div :class="styles.fields()">
          <slot />
          <slot v-if="isExpanded" name="more" />
        </div>

        <UButton
          v-if="hasMore"
          :class="styles.addCriteria()"
          variant="link"
          color="primary"
          :icon="isExpanded ? 'i-lucide-minus' : 'i-lucide-plus'"
          @click="toggleExpanded"
        >
          {{ isExpanded ? lessLabel : moreLabel }}
        </UButton>
      </div>

      <footer :class="styles.footer()">
        <slot name="footer">
          <UButton variant="outline" color="primary" @click="emit('cancel')">
            {{ cancelLabel }}
          </UButton>
          <UButton type="submit" color="primary">
            {{ submitLabel }}
          </UButton>
        </slot>
      </footer>
    </form>
  </aside>
</template>
